/**
 * HelcimPlansModal — layout mirrors BookingsListModal exactly.
 * - BasePageWithHeader with transparent toolbar
 * - IonContent with --background: var(--ion-background-color)
 * - Inner IonGrid scroll container (plansContainer)
 * - IonFab morphing search overlay
 * - Tap card → action sheet (Edit / Deactivate / Delete)
 * - FAB add button + Edit open a plan form sheet
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
// Aliased: this file also keeps its own local PortalDropdown (below) for the
// create/edit form's <select>-replacement dropdowns, which deliberately use
// a non-blocking backdrop so scroll/clicks pass through underneath — a real
// behavioral difference from the shared component's blocking backdrop, so
// only the row-menu/status-filter usages (former RowMenu) were converted.
import SharedPortalDropdown from './PortalDropdown';
import {
  IonModal,
  IonButton,
  IonContent,
  IonIcon,
  IonBadge,
  IonSpinner,
  IonSkeletonText,
  IonText,
  IonSearchbar,
  IonFab,
  IonGrid,
  IonToggle,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonRow,
  IonCol,
  IonRadio,
  IonCheckbox,
} from '@ionic/react';
import {
  refreshOutline,
  searchOutline,
  closeOutline,
  peopleOutline,
  calendarOutline,
  cashOutline,
  cardOutline,
  addOutline,
  ellipsisVertical,
  createOutline,
  trashOutline,
  pauseCircleOutline,
  chevronUpOutline,
  chevronDownOutline,
  informationCircleOutline,
  infiniteOutline,
} from 'ionicons/icons';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import type { HelcimPaymentPlan, CreatePaymentPlanPayload } from '@rsmaids/shared/services/api/helcim/helcim';
import {
  getPaymentPlans,
  getSubscriptions,
  createPaymentPlan,
  updatePaymentPlan,
  deletePaymentPlan,
} from '../services/helcimInvoicingService';
import BasePageWithHeader from './BasePageWithHeader';
import GlassConfirmSheet from './GlassConfirmSheet';
import HelcimPlanDetailModal from './HelcimPlanDetailModal';
import { applySearchOverlayPosition } from '../utils/overlayPosition';
import styles from './HelcimPlansModal.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToContact?: (contactId: string) => void;
}

// The status filter and per-row action menu both now use the shared
// PortalDropdown (see the .tsx import) instead of a locally duplicated
// RowMenu — its option shape carries `selected`/`role` instead of this
// file's former `value === value` / `destructive` convention.

// ─── Create/Edit form modal — portal-anchored dropdown (verbatim mechanism
// from AddSubscriberModal.tsx's usePortalDropdownPosition/PortalDropdown),
// replacing this form's native <select> elements with the app's standard
// pill-trigger + glass dropdown card. Uses its own backdrop class
// (fDropdownBackdrop, pointer-events:none) rather than the list view's
// RowMenu .dropdownBackdrop (pointer-events:auto) above — see that class's
// comment in the .scss file for why. ──────────────────────────────────────

interface DropdownOption {
  label: string;
  value: string;
  sublabel?: string;
}

type PortalDropdownRect = { top: number; left?: number; right?: number; width?: number };

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  matchWidth = true,
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      const top = box.bottom - anchor.top + 4;
      return { top, left: box.left - anchor.left, width: matchWidth ? box.width : undefined };
    };
    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current as HTMLElement | null;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => { const r = computeRect(); if (r) applyRect(r); };
    const handleResize = () => { const r = computeRect(); if (r) { setRect(r); applyRect(r); } };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, matchWidth]);

  return rect;
}

const PortalDropdown: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: DropdownOption[];
  value?: string;
  onSelect: (value: string) => void;
  matchWidth?: boolean;
}> = ({ isOpen, anchorRef, portalRef, onClose, options, value, onSelect, matchWidth = true }) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePortalDropdownPosition(isOpen, anchorRef, portalRef, cardRef, onClose, matchWidth);

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <>
      <div className={styles.fDropdownBackdrop} onClick={onClose} />
      <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
        <IonList lines="none" className={styles.customDropdownListInner}>
          {options.map(opt => (
            <IonItem
              key={opt.value}
              button
              lines="none"
              detail={false}
              className={`${styles.customDropdownItem} ${opt.value === value ? styles.customDropdownItemActive : ''}`}
              onClick={() => onSelect(opt.value)}
            >
              <IonLabel>{opt.label}</IonLabel>
              {opt.sublabel && <IonText slot="end" className={styles.customDropdownSublabel}>{opt.sublabel}</IonText>}
            </IonItem>
          ))}
        </IonList>
      </IonCard>
    </>,
    portalNode,
  );
};

function optLabel(opts: DropdownOption[], value: string): string {
  return opts.find(o => o.value === value)?.label ?? value;
}

const TRIAL_UNIT_OPTIONS: DropdownOption[] = [
  { value: 'days', get label() { return i18n.t('components.HelcimPlansModal.day2'); } },
  { value: 'weeks', get label() { return i18n.t('components.HelcimPlansModal.weeks'); } },
];

const PERIOD_OPTIONS: DropdownOption[] = [
  { value: 'daily', get label() { return i18n.t('components.HelcimPlansModal.daily'); } },
  { value: 'weekly', get label() { return i18n.t('components.HelcimPlansModal.weekly'); } },
  { value: 'monthly', get label() { return i18n.t('components.HelcimPlansModal.monthly'); } },
  { value: 'yearly', get label() { return i18n.t('components.HelcimPlansModal.yearly'); } },
  { value: 'custom', get label() { return i18n.t('components.HelcimPlansModal.custom'); } },
];

const CUSTOM_PERIOD_OPTIONS: DropdownOption[] = [
  { value: 'daily', get label() { return i18n.t('components.HelcimPlansModal.dayS'); } },
  { value: 'weekly', get label() { return i18n.t('components.HelcimPlansModal.weekS'); } },
  { value: 'monthly', get label() { return i18n.t('components.HelcimPlansModal.month2'); } },
  { value: 'yearly', get label() { return i18n.t('components.HelcimPlansModal.yearS'); } },
];

const CHARGE_OCCURS_OPTIONS: DropdownOption[] = [
  { value: 'first_payment', get label() { return i18n.t('components.HelcimPlansModal.withTheFirstPayment'); } },
  { value: 'signup', get label() { return i18n.t('components.HelcimPlansModal.onSignUp'); } },
];

const WEEKDAY_OPTIONS: DropdownOption[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  .map(d => ({ value: d, label: d }));

const MONTH_OPTIONS: DropdownOption[] = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
  .map((m, i) => ({ value: m, label: new Date(2000, i).toLocaleString('default', { month: 'long' }) }));

const MONTH_DAY_OPTIONS: DropdownOption[] = Array.from({ length: 28 }, (_, i) => String(i + 1).padStart(2, '0'))
  .map(d => ({ value: d, label: String(parseInt(d, 10)) }));

const TAX_CALC_OPTIONS: DropdownOption[] = [
  { value: 'full', get label() { return i18n.t('components.HelcimPlansModal.fullTax'); } },
  { value: 'state', get label() { return i18n.t('components.HelcimPlansModal.stateProvinceOnly'); } },
  { value: 'country', get label() { return i18n.t('components.HelcimPlansModal.countryOnly'); } },
];

const HelcimPlansModal: React.FC<Props> = ({ isOpen, onClose, onNavigateToContact }) => {
  const { t: tr } = useTranslation();
  const [plans, setPlans] = useState<HelcimPaymentPlan[]>([]);
  const [filtered, setFiltered] = useState<HelcimPaymentPlan[]>([]);
  const [merchantTaxRate, setMerchantTaxRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('active');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusTriggerRef = useRef<HTMLButtonElement>(null);

  // Plan actions
  const [selectedPlan, setSelectedPlan] = useState<HelcimPaymentPlan | null>(null);
  const selectedPlanRef = useRef<HelcimPaymentPlan | null>(null);
  useEffect(() => { selectedPlanRef.current = selectedPlan; }, [selectedPlan]);
  const [detailOpen, setDetailOpen] = useState(false);
  // Per-row ellipsis action menu — one shared dropdown, retargeted to
  // whichever row's trigger was last clicked, instead of one
  // CardActionsPopover per row.
  const [rowMenuPlanId, setRowMenuPlanId] = useState<number | null>(null);
  const rowMenuTriggerRef = useRef<HTMLIonButtonElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formSaving, setFormSaving] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [pricingChoice, setPricingChoice] = useState<'future' | 'all'>('future');
  const [formName, setFormName] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formPeriod, setFormPeriod] = useState<string>('monthly');
  const [formIncrements, setFormIncrements] = useState('1');
  const [formDesc, setFormDesc] = useState('');
  const [formSetupFee, setFormSetupFee] = useState('');
  const [formChargeOccurs, setFormChargeOccurs] = useState('first_payment');
  const [formTrialPeriod, setFormTrialPeriod] = useState(false);
  const [formTrialAmount, setFormTrialAmount] = useState('1');
  const [formTrialUnit, setFormTrialUnit] = useState<'days' | 'weeks'>('days');
  const [origTrial, setOrigTrial] = useState<{ period: boolean; amount: string; unit: 'days' | 'weeks' } | null>(null);
  const [formBillingDay, setFormBillingDay] = useState<'signup' | 'pick'>('signup');
  const [formPickDayNum, setFormPickDayNum] = useState('1');
  const [formPickWeekday, setFormPickWeekday] = useState('Monday');
  const [formPickMonthDay, setFormPickMonthDay] = useState('01');
  const [formPickMonth, setFormPickMonth] = useState('01');
  const [dayPickerOpen, setDayPickerOpen] = useState(false);
  const [formIsProrated, setFormIsProrated] = useState(true);
  const [formTermType, setFormTermType] = useState<'forever' | 'expires'>('forever');
  const [formTermLength, setFormTermLength] = useState('1');
  const [formTaxType, setFormTaxType] = useState<'no_tax' | 'customer' | 'merchant'>('no_tax');
  const [formTaxCalc, setFormTaxCalc] = useState('full');
  const [formPayCard, setFormPayCard] = useState(true);
  const [formPayACH, setFormPayACH] = useState(true);
  const [formNotifRecurring, setFormNotifRecurring] = useState(false);
  const [formBusinessEmail, setFormBusinessEmail] = useState('');
  const [formMoreOpen, setFormMoreOpen] = useState(false);

  // ── Create/Edit form modal — portal-anchored dropdowns replacing this
  // form's native <select> elements, and standalone-<ion-radio> refs
  // replacing the old plain radio-dot spans. usePortalDropdownPosition/
  // PortalDropdown are copied verbatim above (module scope). The portal root
  // below is rendered as a sibling of this modal's own <IonContent>, not
  // nested inside it — same reasoning as the list view's pickerPortalRoot:
  // IonContent's internal scroll containment traps position:fixed
  // descendants nested inside it instead of letting them escape to the true
  // viewport, which is what these portaled dropdown cards need.
  const fPickerPortalRef = useRef<HTMLDivElement>(null);

  const [showTrialUnitDropdown, setShowTrialUnitDropdown] = useState(false);
  const trialUnitTriggerRef = useRef<HTMLButtonElement>(null);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const periodTriggerRef = useRef<HTMLButtonElement>(null);
  const [showCustomPeriodDropdown, setShowCustomPeriodDropdown] = useState(false);
  const customPeriodTriggerRef = useRef<HTMLButtonElement>(null);
  const [showChargeOccursDropdown, setShowChargeOccursDropdown] = useState(false);
  const chargeOccursTriggerRef = useRef<HTMLButtonElement>(null);
  const [showWeekdayDropdown, setShowWeekdayDropdown] = useState(false);
  const weekdayTriggerRef = useRef<HTMLButtonElement>(null);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const monthTriggerRef = useRef<HTMLButtonElement>(null);
  const [showMonthDayDropdown, setShowMonthDayDropdown] = useState(false);
  const monthDayTriggerRef = useRef<HTMLButtonElement>(null);
  const [showTaxCalcDropdown, setShowTaxCalcDropdown] = useState(false);
  const taxCalcTriggerRef = useRef<HTMLButtonElement>(null);

  // Day-of-month grid — same portal mechanism as the option-list dropdowns
  // above, but with bespoke grid content instead of PortalDropdown's option
  // list, so it's wired to usePortalDropdownPosition directly (same pattern
  // as AddSubscriberModal.tsx's activation-date MiniCalendarPicker dropdown).
  const dayPickerTriggerRef = useRef<HTMLButtonElement>(null);
  const dayPickerCardRef = useRef<HTMLIonCardElement>(null);
  const closeDayPicker = useCallback(() => setDayPickerOpen(false), []);
  const dayPickerRect = usePortalDropdownPosition(dayPickerOpen, dayPickerTriggerRef, fPickerPortalRef, dayPickerCardRef, closeDayPicker, false);
  // fPickerPortalRef's target (the pickerPortalRoot div below) renders
  // alongside this component, so its .current is only reliably non-null
  // after this component has committed at least once — track that in state
  // instead of reading .current directly during render.
  const [fPickerPortalNode, setFPickerPortalNode] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    setFPickerPortalNode(fPickerPortalRef.current);
  }, []);

  // Standalone <ion-radio> refs (no IonRadioGroup) — checked state set
  // imperatively via ref, same pattern/reasoning as AddSubscriberModal.tsx's
  // creditRadioRef/achRadioRef (ion-radio's TS type omits `checked` even
  // though the underlying custom element genuinely has it).
  const signupRadioRef = useRef<HTMLIonRadioElement>(null);
  const pickDayRadioRef = useRef<HTMLIonRadioElement>(null);
  const foreverRadioRef = useRef<HTMLIonRadioElement>(null);
  const expiresRadioRef = useRef<HTMLIonRadioElement>(null);
  const taxNoRadioRef = useRef<HTMLIonRadioElement>(null);
  const taxCustomerRadioRef = useRef<HTMLIonRadioElement>(null);
  const taxMerchantRadioRef = useRef<HTMLIonRadioElement>(null);
  const pricingFutureRadioRef = useRef<HTMLIonRadioElement>(null);
  const pricingAllRadioRef = useRef<HTMLIonRadioElement>(null);

  useEffect(() => {
    if (signupRadioRef.current) (signupRadioRef.current as unknown as { checked: boolean }).checked = formBillingDay === 'signup';
    if (pickDayRadioRef.current) (pickDayRadioRef.current as unknown as { checked: boolean }).checked = formBillingDay === 'pick';
  }, [formBillingDay]);

  useEffect(() => {
    if (foreverRadioRef.current) (foreverRadioRef.current as unknown as { checked: boolean }).checked = formTermType === 'forever';
    if (expiresRadioRef.current) (expiresRadioRef.current as unknown as { checked: boolean }).checked = formTermType === 'expires';
  }, [formTermType]);

  useEffect(() => {
    if (taxNoRadioRef.current) (taxNoRadioRef.current as unknown as { checked: boolean }).checked = formTaxType === 'no_tax';
    if (taxCustomerRadioRef.current) (taxCustomerRadioRef.current as unknown as { checked: boolean }).checked = formTaxType === 'customer';
    if (taxMerchantRadioRef.current) (taxMerchantRadioRef.current as unknown as { checked: boolean }).checked = formTaxType === 'merchant';
  }, [formTaxType]);

  useEffect(() => {
    if (pricingFutureRadioRef.current) (pricingFutureRadioRef.current as unknown as { checked: boolean }).checked = pricingChoice === 'future';
    if (pricingAllRadioRef.current) (pricingAllRadioRef.current as unknown as { checked: boolean }).checked = pricingChoice === 'all';
  }, [pricingChoice]);

  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const scrollRef = useRef<HTMLIonGridElement>(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const loadPlans = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getPaymentPlans(),
      getSubscriptions({ skipEnrichment: true }),
    ])
      .then(([{ plans: data, merchantTaxRate: rate }, { subscriptions: subs }]) => {
        // Compute subscriber counts client-side — avoids extra Helcim API call in CF
        const countMap = new Map<number, number>();
        subs.forEach(s => { if (s.paymentPlanId) countMap.set(s.paymentPlanId, (countMap.get(s.paymentPlanId) ?? 0) + 1); });
        const enriched = data.map(p => ({ ...p, subscriberCount: countMap.get(p.id ?? -1) ?? 0 }));
        setPlans(enriched);
        setFiltered(enriched);
        setMerchantTaxRate(rate);
        setSelectedPlan(prev => prev ? (enriched.find(p => p.id === prev.id) ?? prev) : null);
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  // Silent refresh — updates plan list without showing the full-screen loader
  const loadPlansSilent = useCallback(() => {
    Promise.all([
      getPaymentPlans(),
      getSubscriptions({ skipEnrichment: true }),
    ])
      .then(([{ plans: data, merchantTaxRate: rate }, { subscriptions: subs }]) => {
        const countMap = new Map<number, number>();
        subs.forEach(s => { if (s.paymentPlanId) countMap.set(s.paymentPlanId, (countMap.get(s.paymentPlanId) ?? 0) + 1); });
        const enriched = data.map(p => ({ ...p, subscriberCount: countMap.get(p.id ?? -1) ?? 0 }));
        setPlans(enriched);
        setFiltered(enriched);
        setMerchantTaxRate(rate);
        setSelectedPlan(prev => prev ? (enriched.find(p => p.id === prev.id) ?? prev) : null);
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — loadPlans's own setLoading(true)/setError(null)
    // are needed on every real invocation (including the retry/reselect
    // call sites elsewhere), so they can't just be dropped the way a
    // mount-only redundant reset could be.
    Promise.resolve().then(() => loadPlans());
  }, [isOpen, loadPlans]);

  // Re-derives `filtered` from plans/searchText/statusFilter — computed
  // during render (React's "adjust state when a dependency changes"
  // pattern) rather than as a setState call inside an effect. `filtered`
  // still stays real state (not folded into a useMemo) because it's also
  // updated directly elsewhere with incremental edits (delete, optimistic
  // update) that this re-filter must build on top of, not overwrite.
  const [prevFilterInputs, setPrevFilterInputs] = useState({ searchText, statusFilter, plans });
  if (
    prevFilterInputs.searchText !== searchText ||
    prevFilterInputs.statusFilter !== statusFilter ||
    prevFilterInputs.plans !== plans
  ) {
    setPrevFilterInputs({ searchText, statusFilter, plans });
    let next = plans;
    if (statusFilter) {
      next = next.filter(p => (p.status ?? '').toLowerCase() === statusFilter);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      next = next.filter(p =>
        (p.name ?? '').toLowerCase().includes(q) || String(p.id ?? '').includes(q)
      );
    }
    setFiltered(next);
  }

  // ── Overlay positioning ───────────────────────────────────────────────────
  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  // ── Keyboard handling ────────────────────────────────────────────────────
  useEffect(() => {
    let showH: { remove: () => Promise<void> } | null = null;
    let hideH: { remove: () => Promise<void> } | null = null;
    let bottomAnimRaf = 0;
    let currentBottom = 0;

    const setBottomOffset = (offset: number) => {
      const el = overlayRef.current;
      if (!el) return;
      if (offset > 0) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.style.removeProperty('transform');
      } else {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transform');
      }
    };

    const animateBottomTo = (h: number) => {
      const targetBottom = h > 0 ? h + KEYBOARD_GAP_PX : 0;
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);
        if (t < 1) bottomAnimRaf = requestAnimationFrame(step);
        else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };
      bottomAnimRaf = requestAnimationFrame(step);
    };

    const applyKb = (h: number) => {
      animateBottomTo(h);
    };

    try {
      Keyboard.addListener('keyboardWillShow', i => applyKb(i.keyboardHeight || 0)).then(h => { showH = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidShow', i => applyKb(i.keyboardHeight || 0)).catch(() => { });
      Keyboard.addListener('keyboardWillHide', () => applyKb(0)).then(h => { hideH = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidHide', () => applyKb(0)).catch(() => { });
    } catch { }

    const vv = window.visualViewport;
    let raf = 0;
    const onVv = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!vv) return;
        const off = window.innerHeight - vv.height - vv.offsetTop;
        applyKb(off > 50 ? off : 0);
      });
    };
    if (vv) vv.addEventListener('resize', onVv);

    return () => {
      const el = overlayRef.current;
      if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (raf) cancelAnimationFrame(raf);
      if (vv) vv.removeEventListener('resize', onVv);
      try { showH?.remove(); } catch { }
      try { hideH?.remove(); } catch { }
    };
  }, []);

  // ── Toggle search ────────────────────────────────────────────────────────
  const handleToggleSearch = () => {
    if (isSearchVisible) {
      setSearchText('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => { setIsSearchHidden(true); setIsSearchClosing(false); }, 220);
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const g = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (g) g().then(el => { try { el.blur(); } catch { } try { Keyboard.hide(); } catch { } }).catch(() => { try { Keyboard.hide(); } catch { } });
        else { try { Keyboard.hide(); } catch { } }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
          const s = searchRef.current as HTMLIonSearchbarElement | null;
          const g = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
          if (g) g().then(el => { try { el.focus(); } catch { } }).catch(() => { });
        } catch { }
      }));
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const fmt = (val?: number, currency?: string) =>
    val != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'USD' }).format(val) : null;

  const pricingLabel = (plan: HelcimPaymentPlan) => {
    const amount = fmt(plan.recurringAmount, plan.currency) ?? '$0.00';
    const period = (plan.billingPeriod ?? '').toLowerCase();
    const inc = plan.billingPeriodIncrements ?? 1;
    const map: Record<string, string> = { daily: 'day', weekly: 'wk', monthly: 'mo', yearly: 'yr' };
    const p = map[period] ?? period;
    return inc > 1 ? `${amount} / ${inc} ${p}` : `${amount} / ${p}`;
  };

  const billingDayLabel = (plan: HelcimPaymentPlan) => {
    const d = plan.dateBilling ?? (plan['dateBilling'] as string | undefined);
    if (!d || d === 'Sign-up') return tr('components.HelcimPlansModal.onSignUp');
    const inc = plan.billingPeriodIncrements ?? 1;
    if ((plan.billingPeriod ?? '').toLowerCase() === 'weekly' && inc > 1) return tr('components.HelcimPlansModal.everyWks', { d, inc });
    return d;
  };

  // ── Plan action helpers ───────────────────────────────────────────────────
  const openCreate = () => {
    setFormMode('create');
    setFormName('');
    setFormAmount('');
    setFormPeriod('monthly');
    setFormIncrements('1');
    setFormDesc('');
    setFormSetupFee('');
    setFormChargeOccurs('first_payment');
    setFormTrialPeriod(false);
    setOrigTrial(null);
    setFormTrialAmount('1');
    setFormTrialUnit('days');
    setFormBillingDay('signup');
    setFormPickDayNum('1');
    setFormPickWeekday('Monday');
    setFormPickMonthDay('01');
    setFormPickMonth('01');
    setFormIsProrated(true);
    setFormTermType('forever');
    setFormTermLength('1');
    setFormTaxType('no_tax');
    setFormTaxCalc('full');
    setFormPayCard(true);
    setFormPayACH(true);
    setFormNotifRecurring(false);
    setFormBusinessEmail('');
    setFormMoreOpen(false);
    setFormOpen(true);
  };

  const openEdit = (plan: HelcimPaymentPlan) => {
    setFormMode('edit');
    setFormName(plan.name ?? '');
    setFormAmount(String(plan.recurringAmount ?? ''));
    setFormPeriod((plan.billingPeriod ?? 'monthly').toLowerCase());
    setFormIncrements(String(plan.billingPeriodIncrements ?? 1));
    setFormDesc((plan.description as string | undefined) ?? '');
    setFormSetupFee(String((plan as Record<string, unknown>).setupAmount ?? ''));
    setFormChargeOccurs((plan.billSetupImmediately ?? 'first_billing') === 'immediate' ? 'signup' : 'first_payment');
    const trialRaw = Number((plan as Record<string, unknown>).freeTrialPeriod ?? 0);
    setFormTrialPeriod(trialRaw > 0);
    if (trialRaw > 0 && trialRaw % 7 === 0) {
      setFormTrialAmount(String(trialRaw / 7));
      setFormTrialUnit('weeks');
      setOrigTrial({ period: true, amount: String(trialRaw / 7), unit: 'weeks' });
    } else {
      setFormTrialAmount(trialRaw > 0 ? String(trialRaw) : '1');
      setFormTrialUnit('days');
      setOrigTrial({ period: trialRaw > 0, amount: trialRaw > 0 ? String(trialRaw) : '1', unit: 'days' });
    }
    const dbDay = (plan.dateBilling ?? '') as string;
    setFormBillingDay(dbDay && dbDay !== 'Sign-up' && dbDay !== 'Daily' ? 'pick' : 'signup');
    setFormIsProrated((plan as Record<string, unknown>).isProrated !== 'no');
    setFormPickDayNum(dbDay && /^\d+$/.test(dbDay) ? String(parseInt(dbDay)) : '1');
    setFormPickWeekday(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(dbDay) ? dbDay : 'Monday');
    if (dbDay && dbDay.includes('-')) { const [m, d] = dbDay.split('-'); setFormPickMonth(m ?? '01'); setFormPickMonthDay(d ?? '01'); }
    const termRaw = (plan as Record<string, unknown>).termType as string | undefined;
    const termLenRaw = (plan as Record<string, unknown>).termLength as number | undefined;
    setFormTermType(termRaw === 'expires' ? 'expires' : 'forever');
    setFormTermLength(String(termLenRaw ?? 1));
    const taxRaw = (plan as Record<string, unknown>).taxType as string | undefined;
    setFormTaxType(taxRaw === 'customer' ? 'customer' : taxRaw === 'merchant' ? 'merchant' : 'no_tax');
    const calcRaw = plan.taxCalculation as string | undefined;
    setFormTaxCalc(calcRaw === 'province_only' ? 'state' : calcRaw === 'country_only' ? 'country' : 'full');
    const pm = (plan as Record<string, unknown>).paymentMethod as string | undefined;
    setFormPayCard(pm !== 'bank');
    setFormPayACH(pm !== 'card');
    setFormNotifRecurring(!!(plan.businessEmail));
    setFormBusinessEmail(plan.businessEmail ?? '');
    setFormMoreOpen(false);
    setSelectedPlan(plan);
    setFormOpen(true);
  };

  const handleSaveForm = async (updateExisting?: boolean) => {
    if (!formName.trim()) return;
    // In edit mode, if price changed, show dialog first
    if (formMode === 'edit' && updateExisting === undefined) {
      const origAmount = selectedPlanRef.current?.recurringAmount;
      if (origAmount !== undefined && parseFloat(formAmount) !== origAmount) {
        setPricingChoice('future');
        setPricingDialogOpen(true);
        return;
      }
    }
    setFormSaving(true);
    try {
      const payload: CreatePaymentPlanPayload = {
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        type: (formBillingDay === 'pick' ? 'cycle' : 'subscription') as 'subscription' | 'cycle',
        ...(formBillingDay === 'pick' ? { isProrated: formIsProrated ? 'yes' : 'no' } : {}),
        billingPeriod: formPeriod as CreatePaymentPlanPayload['billingPeriod'],
        billingPeriodIncrements: parseInt(formIncrements) || 1,
        recurringAmount: parseFloat(formAmount) || 0,
        currency: 'USD' as const,
        ...(formSetupFee.trim() ? { setupAmount: parseFloat(formSetupFee) || 0 } : {}),
        dateBilling: (() => {
          if (formBillingDay === 'signup') return 'Sign-up';
          const period = formPeriod.toLowerCase();
          if (period === 'daily') return 'Daily';
          if (period === 'weekly') return formPickWeekday;
          if (period === 'yearly') return `${formPickMonth}-${formPickMonthDay}`;
          // monthly: DD format
          return formPickDayNum === 'last' ? 'Last' : String(parseInt(formPickDayNum) || 1).padStart(2, '0');
        })(),
        termType: formTermType,
        ...(formTermType === 'expires' ? { termLength: parseInt(formTermLength) || 1 } : {}),
        ...(formSetupFee.trim() && parseFloat(formSetupFee) > 0 ? { billSetupImmediately: formChargeOccurs === 'signup' ? 'immediate' as const : 'first_billing' as const } : {}),
        freeTrialPeriod: formTrialPeriod ? (parseInt(formTrialAmount) || 1) * (formTrialUnit === 'weeks' ? 7 : 1) : 0,
        taxType: formTaxType as CreatePaymentPlanPayload['taxType'],
        ...(formTaxType === 'merchant' ? { taxCalculation: formTaxCalc === 'state' ? 'province_only' : formTaxCalc === 'country' ? 'country_only' : 'country_province' as CreatePaymentPlanPayload['taxCalculation'] } : {}),
        paymentMethod: formPayCard && formPayACH ? 'card_bank' : formPayCard ? 'card' : formPayACH ? 'bank' : undefined,
        emailSettingRecurringQueue: formNotifRecurring ? 'merchant' : 'off',
        businessEmail: formNotifRecurring ? formBusinessEmail.trim() : '',
      };
      if (formMode === 'create') {
        const created = await createPaymentPlan(payload);
        console.log('[handleSaveForm] create payload:', JSON.stringify(payload));
        console.log('[handleSaveForm] create response:', JSON.stringify(created));
      } else if (selectedPlan?.id) {
        await updatePaymentPlan(selectedPlan.id, {
          ...payload,
          ...(updateExisting !== undefined ? { updateExistingSubscriptions: updateExisting } : {}),
        });
      }
      setFormOpen(false);
      loadPlans();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPlan?.id) return;
    const id = selectedPlan.id;
    // Optimistic: remove immediately so the list updates instantly
    setPlans(prev => prev.filter(p => p.id !== id));
    setFiltered(prev => prev.filter(p => p.id !== id));
    // Fire delete in background, then silent-sync
    deletePaymentPlan(id).then(() => loadPlansSilent()).catch(() => loadPlansSilent());
  };

  const handleDeactivate = (plan: HelcimPaymentPlan) => {
    if (!plan.id) return;
    const isActive = (plan.status ?? '').toLowerCase() === 'active';
    const newStatus = isActive ? 'inactive' : 'active';

    // Optimistic update — flip status in local state immediately
    const applyStatus = (data: HelcimPaymentPlan[]) =>
      data.map(p => p.id === plan.id ? { ...p, status: newStatus } : p);
    setPlans(prev => applyStatus(prev));
    setFiltered(prev => applyStatus(prev));

    updatePaymentPlan(plan.id, { status: newStatus } as Partial<CreatePaymentPlanPayload>)
      .then(() => loadPlansSilent())   // sync with server once done
      .catch(e => {
        // Revert optimistic update on error
        const revert = (data: HelcimPaymentPlan[]) =>
          data.map(p => p.id === plan.id ? { ...p, status: plan.status } : p);
        setPlans(prev => revert(prev));
        setFiltered(prev => revert(prev));
        setError(e instanceof Error ? e.message : String(e));
      });
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.modal}>
        <div className={styles.modalWrapper}>
          <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
          <BasePageWithHeader
            title={tr('components.HelcimPlansModal.subscriptionPlans')}
            toolbarClassName={styles.toolbar}
            smallTitleClassName={styles.smallTitle}
            titleHeaderClassName={styles.titleHeader}
            startButtons={
              <>
                <IonButton fill="clear" className={styles.closeButton} onClick={onClose}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
                <IonButton style={{ visibility: 'hidden' }}>
                </IonButton>
              </>
            }
            endButtons={
              <>
                <IonButton fill="clear" className={styles.addButton} onClick={openCreate}>
                  <IonIcon icon={addOutline} />
                </IonButton>
                <IonButton fill="clear" className={styles.searchButton} onClick={handleToggleSearch}>
                  <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
                </IonButton>
              </>
            }
          >
            <IonRow className={styles.filterBar}>
              <button
                ref={statusTriggerRef}
                className={styles.periodBtn}
                onClick={() => setShowStatusDropdown(v => !v)}
              >
                {statusFilter === 'active' ? tr('components.HelcimPlansModal.active') : statusFilter === 'inactive' ? tr('components.HelcimPlansModal.inactive') : tr('components.HelcimPlansModal.allStatuses')}
                <IonIcon icon={chevronDownOutline} />
              </button>
            </IonRow>

            <SharedPortalDropdown
              isOpen={showStatusDropdown}
              anchorRef={statusTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setShowStatusDropdown(false)}
              align="start"
              matchWidth={false}
              onSelect={(v) => { setStatusFilter(v as '' | 'active' | 'inactive'); setShowStatusDropdown(false); }}
              options={[
                { value: '', label: tr('components.HelcimPlansModal.allStatuses'), selected: statusFilter === '' },
                { value: 'active', label: tr('components.HelcimPlansModal.active'), selected: statusFilter === 'active' },
                { value: 'inactive', label: tr('components.HelcimPlansModal.inactive'), selected: statusFilter === 'inactive' },
              ]}
            />

            {/* ── IonContent + inner IonGrid scroll container ── */}
            <IonContent className={styles.content}>
              <IonGrid ref={scrollRef} className={styles.plansContainer}>

                {loading && [0, 1, 2, 3, 4].map(i => (
                  <IonCard key={`skeleton-${i}`} className={`${styles.card} ${styles.cardActive}`} aria-hidden="true">
                    <IonCardContent className={styles.cardBody}>
                      <IonItem lines="none" className={styles.cardTopRow}>
                        <IonSkeletonText animated slot="end" className={styles.skeletonBar} style={{ width: 64, height: 22, margin: '3px 0', borderRadius: 11 }} />
                      </IonItem>
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '50%', height: 20, margin: '0 0 14px' }} />
                      <IonGrid className={styles.statsRow}>
                        <IonRow>
                          {[0, 1, 2].map(j => (
                            <IonCol key={j} className={styles.stat}>
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 16, height: 16, margin: '0 0 1px', borderRadius: 4 }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '65%', height: 13, margin: '1px 0' }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '45%', height: 10, margin: '1px 0' }} />
                            </IonCol>
                          ))}
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                ))}

                {!loading && error && (
                  <div className={styles.center}>
                    <IonText color="danger">{error}</IonText>
                    <IonButton fill="clear" onClick={loadPlans}>
                      <IonIcon slot="start" icon={refreshOutline} />{tr('components.HelcimPlansModal.retry')}
                    </IonButton>
                  </div>
                )}

                {!loading && !error && filtered.length === 0 && (
                  <div className={styles.center}>
                    <IonText color="medium">{tr('components.HelcimPlansModal.noPlansFound')}</IonText>
                  </div>
                )}

                {!loading && !error && filtered.length > 0 && filtered.map(plan => {
                  const status = (plan.status ?? '').toLowerCase();
                  const isActive = status === 'active';
                  const subscribers = plan.subscriberCount ?? (plan['subscriberCount'] as number | undefined) ?? 0;
                  const setupFee = plan.setupAmount != null && plan.setupAmount > 0 ? fmt(plan.setupAmount, plan.currency)! : null;

                  return (
                    <IonCard
                      key={plan.id}
                      className={`${styles.card} ${isActive ? styles.cardActive : styles.cardInactive}`}
                      onClick={(e: React.MouseEvent) => {
                    
                        const path = e.nativeEvent.composedPath();
                        if (path.some(el => el instanceof HTMLElement && el.hasAttribute('data-row-menu-btn'))) {
                          return;
                        }
                        setSelectedPlan(plan); loadPlans(); setDetailOpen(true);
                      }}
                    >
                      <IonCardContent className={styles.cardBody}>
                        <IonItem lines="none" className={styles.cardTopRow}>
                          <IonBadge slot="end" className={`${styles.badge} ${isActive ? styles.badgeSuccess : styles.badgeDanger}`}>
                            {translateDynamic(plan.status ?? 'unknown')}
                          </IonBadge>
                          <IonButton
                            fill="clear"
                            slot="end"
                            className={styles.cardMenuBtn}
                            data-row-menu-btn="true"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              rowMenuTriggerRef.current = e.currentTarget;
                              setSelectedPlan(plan);
                              setRowMenuPlanId(plan.id ?? null);
                            }}
                          >
                            <IonIcon icon={ellipsisVertical} slot="icon-only" className={styles.cardMenuIcon} />
                          </IonButton>
                        </IonItem>
                        <h2 className={styles.cardName}>{plan.name ?? tr('components.HelcimPlansModal.plan', { id: plan.id })}</h2>

                        <IonGrid className={`${styles.statsRow}${setupFee ? ` ${styles.statsRowCompact}` : ''}`}>
                          <IonRow>
                            <IonCol className={styles.stat}>
                              <IonIcon icon={cashOutline} className={`${styles.statIcon} ${styles.statIconGreen}`} />
                              <IonText className={styles.statValue}>{pricingLabel(plan)}</IonText>
                              <IonText className={styles.statLabel}>{tr('components.HelcimPlansModal.pricing')}</IonText>
                            </IonCol>
                            <IonCol className={styles.stat}>
                              <IonIcon icon={calendarOutline} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                              <IonText className={styles.statValue}>{billingDayLabel(plan)}</IonText>
                              <IonText className={styles.statLabel}>{tr('components.HelcimPlansModal.billingDay')}</IonText>
                            </IonCol>
                            <IonCol className={styles.stat}>
                              <IonIcon icon={peopleOutline} className={`${styles.statIcon} ${styles.statIconPurple}`} />
                              <IonText className={styles.statValue}>{subscribers}</IonText>
                              <IonText className={styles.statLabel}>{tr('components.HelcimPlansModal.subscribers')}</IonText>
                            </IonCol>
                            {setupFee && (
                              <IonCol className={styles.stat}>
                                <IonIcon icon={cardOutline} className={`${styles.statIcon} ${styles.statIconAmber}`} />
                                <IonText className={styles.statValue}>{setupFee}</IonText>
                                <IonText className={styles.statLabel}>{tr('components.HelcimPlansModal.setupFee')}</IonText>
                              </IonCol>
                            )}
                          </IonRow>
                        </IonGrid>
                      </IonCardContent>
                    </IonCard>
                  );
                })}

                <SharedPortalDropdown
                  isOpen={rowMenuPlanId !== null}
                  anchorRef={rowMenuTriggerRef}
                  portalRef={pickerPortalRef}
                  onClose={() => setRowMenuPlanId(null)}
                  // Hang from the ellipsis button's right edge (same as HelcimSubscriptionsModal's row menu) —
                  // the default 'start' opens from its left edge, which runs off the right of the screen.
                  align="end"
                  onSelect={(action) => {
                    setRowMenuPlanId(null);
                    const plan = filtered.find(p => p.id === rowMenuPlanId) ?? selectedPlanRef.current;
                    if (!plan) return;
                    if (action === 'edit') openEdit(plan);
                    else if (action === 'toggle') handleDeactivate(plan);
                    else if (action === 'delete') { setSelectedPlan(plan); setDeleteAlertOpen(true); }
                  }}
                  options={(() => {
                    const plan = filtered.find(p => p.id === rowMenuPlanId);
                    const planIsActive = (plan?.status ?? '').toLowerCase() === 'active';
                    return [
                      { value: 'edit', label: tr('components.HelcimPlansModal.editPlan'), icon: createOutline },
                      { value: 'toggle', label: planIsActive ? tr('components.HelcimPlansModal.deactivate') : tr('components.HelcimPlansModal.activate'), icon: pauseCircleOutline },
                      { value: 'delete', label: tr('components.HelcimPlansModal.deletePlan'), icon: trashOutline, role: 'destructive' as const },
                    ];
                  })()}
                />

                {isSearchVisible && <div className={styles.searchBarSpacer} />}
              </IonGrid>
            </IonContent>

            {/* Morphing search overlay — outside IonContent, inside BasePageWithHeader */}
            <IonFab
              ref={el => { overlayRef.current = el; }}
              className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
              aria-hidden={!isSearchVisible}
            >
              <IonSearchbar
                ref={searchRef}
                value={searchText}
                debounce={50}
                onIonInput={e => setSearchText(e.detail.value ?? '')}
                onIonClear={() => setSearchText('')}
                placeholder={tr('components.HelcimPlansModal.searchPlans', { length: plans.length })}
                animated
                showCancelButton="never"
                showClearButton={searchText ? 'always' : 'never'}
                className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
              />
            </IonFab>
          </BasePageWithHeader>

          {/* ── Delete confirmation ── */}
          <GlassConfirmSheet
            isOpen={deleteAlertOpen}
            title={tr('components.HelcimPlansModal.deletePlan')}
            message={tr('components.HelcimPlansModal.permanentlyDeleteThisCannotBeUndone', { v1: selectedPlan?.name ?? 'this plan' })}
            confirmLabel="Delete"
            onConfirm={handleDeleteConfirm}
            onDismiss={() => setDeleteAlertOpen(false)}
          />
        </div>
      </IonModal>

      {/* ── Plan detail modal ── */}
      <HelcimPlanDetailModal
        plan={selectedPlan}
        isOpen={detailOpen}
        allPlans={plans}
        merchantTaxRate={merchantTaxRate}
        onClose={() => setDetailOpen(false)}
        onPlanChanged={() => { loadPlansSilent(); }}
        onPlanUpdated={(updated) => { setSelectedPlan(updated); setPlans(prev => prev.map(p => p.id === updated.id ? updated : p)); setFiltered(prev => prev.map(p => p.id === updated.id ? updated : p)); }}
        onEdit={() => selectedPlanRef.current && openEdit(selectedPlanRef.current)}
        onNavigateToContact={onNavigateToContact ? (contactId) => { setDetailOpen(false); onNavigateToContact(contactId); } : undefined}
      />

      {/* ── Create / Edit form modal (full screen) — restyled to match
      AddSubscriberModal.tsx's glass-morphism conventions: IonCard sections,
      portal-anchored dropdowns replacing native <select>s, standalone-IonRadio
      billing-day/term/tax rows, and the shared toolbar/close-button/save-button
      recipe. ── */}
      <IonModal isOpen={formOpen} onDidDismiss={() => { setFormOpen(false); setFormNotifRecurring(false); setFormBusinessEmail(''); }} className={styles.formModal}>
        <IonRow className={styles.modalWrapper}>
          <BasePageWithHeader
            title={formMode === 'create' ? tr('components.HelcimPlansModal.createANewPlan') : tr('components.HelcimPlansModal.editPlan')}
            toolbarClassName={styles.fToolbar}
            smallTitleClassName={styles.fSmallTitle}
            startButtons={
              <IonButton shape="round" className={styles.fCloseButton} onClick={() => setFormOpen(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            }
            endButtons={
              <IonButton
                fill="solid"
                color="primary"
                className={styles.fSaveBtn}
                disabled={formSaving || !formName.trim()}
                onClick={() => handleSaveForm()}
              >
                {formSaving ? <IonSpinner name="bubbles" color="primary" style={{ width: 16, height: 16 }} /> : tr('components.HelcimPlansModal.save')}
              </IonButton>
            }
          >
            {/* Portal root + anchored dropdowns live outside IonContent, as its
            siblings — same placement/reasoning as AddSubscriberModal.tsx's
            pickerPortalRoot: IonContent's internal scroll containment traps
            position:fixed descendants nested inside it instead of letting them
            escape to the true viewport, which is what these portaled dropdown
            cards need. */}
            <div ref={fPickerPortalRef} className={styles.pickerPortalRoot} />

            <PortalDropdown
              isOpen={showTrialUnitDropdown}
              anchorRef={trialUnitTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowTrialUnitDropdown(false)}
              value={formTrialUnit}
              onSelect={(v) => { setFormTrialUnit(v as 'days' | 'weeks'); setShowTrialUnitDropdown(false); }}
              options={TRIAL_UNIT_OPTIONS}
            />

            <PortalDropdown
              isOpen={showPeriodDropdown}
              anchorRef={periodTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowPeriodDropdown(false)}
              value={formIncrements !== '1' ? 'custom' : formPeriod}
              onSelect={(v) => {
                setShowPeriodDropdown(false);
                if (v === 'custom') { setFormIncrements('2'); }
                else { setFormPeriod(v); setFormIncrements('1'); setFormBillingDay('signup'); }
              }}
              options={PERIOD_OPTIONS}
            />

            <PortalDropdown
              isOpen={showCustomPeriodDropdown}
              anchorRef={customPeriodTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowCustomPeriodDropdown(false)}
              value={formPeriod}
              onSelect={(v) => { setFormPeriod(v); setShowCustomPeriodDropdown(false); }}
              options={CUSTOM_PERIOD_OPTIONS}
            />

            <PortalDropdown
              isOpen={showChargeOccursDropdown}
              anchorRef={chargeOccursTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowChargeOccursDropdown(false)}
              value={formChargeOccurs}
              onSelect={(v) => { setFormChargeOccurs(v); setShowChargeOccursDropdown(false); }}
              options={CHARGE_OCCURS_OPTIONS}
            />

            <PortalDropdown
              isOpen={showWeekdayDropdown}
              anchorRef={weekdayTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowWeekdayDropdown(false)}
              value={formPickWeekday}
              onSelect={(v) => { setFormPickWeekday(v); setShowWeekdayDropdown(false); }}
              options={WEEKDAY_OPTIONS}
            />

            <PortalDropdown
              isOpen={showMonthDropdown}
              anchorRef={monthTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowMonthDropdown(false)}
              value={formPickMonth}
              onSelect={(v) => { setFormPickMonth(v); setShowMonthDropdown(false); }}
              options={MONTH_OPTIONS}
            />

            <PortalDropdown
              isOpen={showMonthDayDropdown}
              anchorRef={monthDayTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowMonthDayDropdown(false)}
              value={formPickMonthDay}
              onSelect={(v) => { setFormPickMonthDay(v); setShowMonthDayDropdown(false); }}
              options={MONTH_DAY_OPTIONS}
            />

            <PortalDropdown
              isOpen={showTaxCalcDropdown}
              anchorRef={taxCalcTriggerRef}
              portalRef={fPickerPortalRef}
              onClose={() => setShowTaxCalcDropdown(false)}
              value={formTaxCalc}
              onSelect={(v) => { setFormTaxCalc(v); setShowTaxCalcDropdown(false); }}
              options={TAX_CALC_OPTIONS}
            />

            {/* Day-of-month grid — bespoke portal content (not the generic
            option-list PortalDropdown), same pattern as AddSubscriberModal.tsx's
            activation-date MiniCalendarPicker dropdown. */}
            {dayPickerOpen && dayPickerRect && fPickerPortalNode && createPortal(
              <>
                <div className={styles.fDropdownBackdrop} onClick={() => setDayPickerOpen(false)} />
                <IonCard ref={dayPickerCardRef} className={`${styles.calendarDropdown} ${styles.dayGridDropdown}`} style={{ ...dayPickerRect, position: 'absolute' }}>
                  <p className={styles.fDayGridTitle}>{tr('components.HelcimPlansModal.selectBillingDayS')}</p>
                  <IonRow className={styles.fDayGridCells}>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                      <button
                        key={d}
                        type="button"
                        className={`${styles.fDayCell} ${formPickDayNum === String(d) ? styles.fDayCellActive : ''}`}
                        onClick={() => { setFormPickDayNum(String(d)); setDayPickerOpen(false); }}
                      >{d}</button>
                    ))}
                    <button
                      type="button"
                      className={`${styles.fDayCell} ${styles.fDayCellLast} ${formPickDayNum === 'last' ? styles.fDayCellActive : ''}`}
                      onClick={() => { setFormPickDayNum('last'); setDayPickerOpen(false); }}
                    >{tr('components.HelcimPlansModal.lastDay')}</button>
                  </IonRow>
                </IonCard>
              </>,
              fPickerPortalNode,
            )}

            <IonContent className={styles.fContent}>
              <IonGrid className={styles.fScrollGrid}>

                {/* ── Basic Info ── */}
                <IonCard className={styles.fCard}>
                  <p className={styles.fSectionTitle}>{tr('components.HelcimPlansModal.basicInfo')}</p>
                  <input
                    className={styles.fInput}
                    type="text"
                    placeholder={tr('components.HelcimPlansModal.planName')}
                    value={formName}
                    onChange={e => setFormName(e.target.value)}
                  />
                  <textarea
                    className={`${styles.fInput} ${styles.fTextarea}`}
                    placeholder={tr('components.HelcimPlansModal.planDescription')}
                    value={formDesc}
                    onChange={e => setFormDesc(e.target.value)}
                    rows={3}
                  />
                  <IonRow className={styles.fCheckRow} onClick={() => setFormTrialPeriod(v => !v)}>
                    <IonCheckbox checked={formTrialPeriod} className={styles.fCheckbox} />
                    <span>{tr('components.HelcimPlansModal.thisPlanHasATrialPeriod')}</span>
                  </IonRow>
                  {formTrialPeriod && (
                    <IonRow className={styles.fRow}>
                      <IonRow className={styles.fCurrencyWrap}>
                        <input
                          className={styles.fCurrencyInput}
                          type="number"
                          placeholder="1"
                          min="1"
                          value={formTrialAmount}
                          onChange={e => setFormTrialAmount(e.target.value)}
                        />
                        <label className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.duration')}</label>
                      </IonRow>
                      <button
                        ref={trialUnitTriggerRef}
                        type="button"
                        className={styles.fSelectWrap}
                        onClick={() => setShowTrialUnitDropdown(v => !v)}
                      >
                        <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.unit')}</span>
                        <span className={styles.fSelectValue}>{optLabel(TRIAL_UNIT_OPTIONS, formTrialUnit)}</span>
                        <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                      </button>
                    </IonRow>
                  )}
                  {formMode === 'edit' && origTrial !== null && (
                    formTrialPeriod !== origTrial.period ||
                    formTrialAmount !== origTrial.amount ||
                    formTrialUnit !== origTrial.unit
                  ) && (
                      <p className={styles.fTrialNote}>
                        <IonIcon icon={informationCircleOutline} />{tr('components.HelcimPlansModal.theChangeWillApplyForNew')}
                      </p>
                    )}
                </IonCard>

                {/* ── Pricing ── */}
                <IonCard className={styles.fCard}>
                  <p className={styles.fSectionTitle}>{tr('components.HelcimPlansModal.pricing')}</p>
                  <p className={styles.fSectionSubtitle}>{tr('components.HelcimPlansModal.theAmountTheCustomerWillBe')}</p>

                  {/* Amount + Billing Period row */}
                  <IonRow className={styles.fRow}>
                    <IonRow className={styles.fCurrencyWrap}>
                      <span className={styles.fCurrencySign}>$</span>
                      <input
                        className={styles.fCurrencyInput}
                        type="number"
                        placeholder="0.00"
                        value={formAmount}
                        onChange={e => setFormAmount(e.target.value)}
                        min="0"
                      />
                      <label className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.amount')}</label>
                    </IonRow>
                    <button
                      ref={periodTriggerRef}
                      type="button"
                      disabled={formMode === 'edit'}
                      className={`${styles.fSelectWrap} ${formMode === 'edit' ? styles.fFieldDisabled : ''}`}
                      onClick={() => setShowPeriodDropdown(v => !v)}
                    >
                      <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.billingPeriod')}</span>
                      <span className={styles.fSelectValue}>{optLabel(PERIOD_OPTIONS, formIncrements !== '1' ? 'custom' : formPeriod)}</span>
                      <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                    </button>
                  </IonRow>

                  {/* Custom: Every N + unit row */}
                  {(formIncrements !== '1') && (
                    <IonRow className={`${styles.fRow} ${formMode === 'edit' ? styles.fFieldDisabled : ''}`}>
                      <IonRow className={styles.fCurrencyWrap}>
                        <input
                          className={styles.fCurrencyInput}
                          type="number"
                          placeholder="1"
                          value={formIncrements}
                          disabled={formMode === 'edit'}
                          onChange={e => setFormIncrements(e.target.value)}
                          min="2"
                        />
                        <label className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.every')}</label>
                      </IonRow>
                      <button
                        ref={customPeriodTriggerRef}
                        type="button"
                        disabled={formMode === 'edit'}
                        className={styles.fSelectWrap}
                        onClick={() => setShowCustomPeriodDropdown(v => !v)}
                      >
                        <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.customBillingPeriod')}</span>
                        <span className={styles.fSelectValue}>{optLabel(CUSTOM_PERIOD_OPTIONS, formPeriod)}</span>
                        <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                      </button>
                    </IonRow>
                  )}

                  {/* Set-up fee */}
                  <IonRow className={styles.fSetupFeeLabel}>
                    <span>{tr('components.HelcimPlansModal.setUpFee')}</span>
                    <span className={styles.fOptional}>{tr('components.HelcimPlansModal.optional')}</span>
                  </IonRow>
                  <IonRow className={styles.fRow}>
                    <IonRow className={styles.fCurrencyWrap}>
                      <span className={styles.fCurrencySign}>$</span>
                      <input
                        className={styles.fCurrencyInput}
                        type="number"
                        placeholder="0.00"
                        value={formSetupFee}
                        onChange={e => setFormSetupFee(e.target.value)}
                        min="0"
                      />
                      <label className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.setUpFee2')}</label>
                    </IonRow>
                    <button
                      ref={chargeOccursTriggerRef}
                      type="button"
                      className={styles.fSelectWrap}
                      onClick={() => setShowChargeOccursDropdown(v => !v)}
                    >
                      <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.chargeOccurs')}</span>
                      <span className={styles.fSelectValue}>{optLabel(CHARGE_OCCURS_OPTIONS, formChargeOccurs)}</span>
                      <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                    </button>
                  </IonRow>
                </IonCard>

                {/* ── Billing Schedule ── */}
                <IonCard className={styles.fCard}>
                  <p className={styles.fSectionTitle}>{tr('components.HelcimPlansModal.billingSchedule')}</p>
                  {formMode === 'edit' ? (
                    <>
                      <IonRow className={styles.fBillingDayReadOnly}>
                        <IonRow className={styles.fBillingDayReadOnlyItem}>
                          <p className={styles.fBillingDayReadOnlyLabel}>{tr('components.HelcimPlansModal.billingDay2')}</p>
                          <IonRow className={styles.fBillingDayReadOnlyRow}>
                            <IonIcon icon={calendarOutline} className={styles.fBillingDayReadOnlyIcon} />
                            <IonLabel>
                              <p className={styles.fBillingDayReadOnlyTitle}>
                                {formBillingDay === 'signup' ? tr('components.HelcimPlansModal.onSignUp') : tr('components.HelcimPlansModal.pickADay')}
                              </p>
                              <p className={styles.fBillingDayReadOnlySub}>
                                {formBillingDay === 'signup' ? tr('components.HelcimPlansModal.billedOnDayOfActivation') : tr('components.HelcimPlansModal.billedOnASpecificDay')}
                              </p>
                            </IonLabel>
                          </IonRow>
                        </IonRow>
                        <IonRow className={styles.fBillingDayReadOnlyItem}>
                          <p className={styles.fBillingDayReadOnlyLabel}>{tr('components.HelcimPlansModal.endOfTerm')}</p>
                          <IonRow className={styles.fBillingDayReadOnlyRow}>
                            <IonIcon icon={infiniteOutline} className={styles.fBillingDayReadOnlyIcon} />
                            <IonLabel>
                              <p className={styles.fBillingDayReadOnlyTitle}>{tr('components.HelcimPlansModal.forever')}</p>
                              <p className={styles.fBillingDayReadOnlySub}>{tr('components.HelcimPlansModal.thePlanContinuesUntilCancelled')}</p>
                            </IonLabel>
                          </IonRow>
                        </IonRow>
                      </IonRow>
                      {formBillingDay === 'pick' && (
                        <IonRow className={styles.fToggleRow}>
                          <IonToggle
                            checked={formIsProrated}
                            onIonChange={e => setFormIsProrated(e.detail.checked)}
                            className={styles.fToggle}
                          />
                          <span className={styles.fToggleLabel}>{tr('components.HelcimPlansModal.prorateBilling')}</span>
                        </IonRow>
                      )}
                    </>
                  ) : (
                    <>
                      <p className={styles.fSubHeading}>{tr('components.HelcimPlansModal.billingDay2')}</p>
                      <p className={styles.fSubSubtitle}>{tr('components.HelcimPlansModal.chooseWhenYourCustomersWillBe')}</p>
                      <IonRow className={styles.fBillingDayRow}>
                        <button
                          type="button"
                          className={`${styles.fBillingDayCard} ${formBillingDay === 'signup' ? styles.fBillingDayCardActive : ''}`}
                          onClick={() => setFormBillingDay('signup')}
                        >
                          <IonRadio ref={signupRadioRef} value="signup" className={styles.radioCircle} />
                          <IonRow className={styles.fBillingDayCardText}>
                            <p className={styles.fBillingDayCardTitle}>{tr('components.HelcimPlansModal.onSignUp')}</p>
                            <p className={styles.fBillingDayCardSub}>{tr('components.HelcimPlansModal.billedOnDayOfActivation')}</p>
                          </IonRow>
                        </button>
                        <button
                          type="button"
                          disabled={formPeriod === 'daily'}
                          className={`${styles.fBillingDayCard} ${formBillingDay === 'pick' ? styles.fBillingDayCardActive : ''}`}
                          onClick={() => formPeriod !== 'daily' && setFormBillingDay('pick')}
                        >
                          <IonRadio ref={pickDayRadioRef} value="pick" className={styles.radioCircle} />
                          <IonRow className={styles.fBillingDayCardText}>
                            <p className={styles.fBillingDayCardTitle}>{tr('components.HelcimPlansModal.pickADay')}</p>
                            <p className={styles.fBillingDayCardSub}>{tr('components.HelcimPlansModal.billedOnASpecificDay')}</p>
                          </IonRow>
                        </button>
                      </IonRow>
                      {formBillingDay === 'pick' && formPeriod === 'monthly' && (
                        <button
                          ref={dayPickerTriggerRef}
                          type="button"
                          className={styles.fDayPickerTrigger}
                          onClick={() => setDayPickerOpen(v => !v)}
                        >
                          <IonIcon icon={calendarOutline} className={styles.fDayPickerTriggerIcon} />
                          <span>{formPickDayNum === 'last' ? tr('components.HelcimPlansModal.lastDay') : tr('components.HelcimPlansModal.day3', { formPickDayNum })}</span>
                        </button>
                      )}
                      {formBillingDay === 'pick' && formPeriod === 'weekly' && (
                        <button
                          ref={weekdayTriggerRef}
                          type="button"
                          className={styles.fSelectWrapFull}
                          onClick={() => setShowWeekdayDropdown(v => !v)}
                        >
                          <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.dayOfWeek')}</span>
                          <span className={styles.fSelectValue}>{formPickWeekday}</span>
                          <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                        </button>
                      )}
                      {formBillingDay === 'pick' && formPeriod === 'yearly' && (
                        <IonRow className={styles.fRow}>
                          <button
                            ref={monthTriggerRef}
                            type="button"
                            className={styles.fSelectWrap}
                            onClick={() => setShowMonthDropdown(v => !v)}
                          >
                            <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.month')}</span>
                            <span className={styles.fSelectValue}>{optLabel(MONTH_OPTIONS, formPickMonth)}</span>
                            <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                          </button>
                          <button
                            ref={monthDayTriggerRef}
                            type="button"
                            className={styles.fSelectWrap}
                            onClick={() => setShowMonthDayDropdown(v => !v)}
                          >
                            <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.day')}</span>
                            <span className={styles.fSelectValue}>{optLabel(MONTH_DAY_OPTIONS, formPickMonthDay)}</span>
                            <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                          </button>
                        </IonRow>
                      )}

                      {/* ── Prorate billing ── */}
                      {formBillingDay === 'pick' && (
                        <IonRow className={styles.fToggleRow}>
                          <IonToggle
                            checked={formIsProrated}
                            onIonChange={e => setFormIsProrated(e.detail.checked)}
                            className={styles.fToggle}
                          />
                          <span className={styles.fToggleLabel}>{tr('components.HelcimPlansModal.prorateBilling')}</span>
                        </IonRow>
                      )}

                      {/* ── End of Term ── */}
                      <IonRow className={styles.fSubDivider} />
                      <p className={styles.fSubHeading}>{tr('components.HelcimPlansModal.endOfTerm')}</p>
                      <p className={styles.fSubSubtitle}>{tr('components.HelcimPlansModal.chooseWhenYourCustomersWillBe')}</p>
                      <IonRow className={styles.fBillingDayRow}>
                        <button
                          type="button"
                          className={`${styles.fBillingDayCard} ${formTermType === 'forever' ? styles.fBillingDayCardActive : ''}`}
                          onClick={() => setFormTermType('forever')}
                        >
                          <IonRadio ref={foreverRadioRef} value="forever" className={styles.radioCircle} />
                          <IonRow className={styles.fBillingDayCardText}>
                            <p className={styles.fBillingDayCardTitle}>{tr('components.HelcimPlansModal.forever')}</p>
                            <p className={styles.fBillingDayCardSub}>{tr('components.HelcimPlansModal.thePlanContinuesUntilCancelled')}</p>
                          </IonRow>
                        </button>
                        <button
                          type="button"
                          className={`${styles.fBillingDayCard} ${formTermType === 'expires' ? styles.fBillingDayCardActive : ''}`}
                          onClick={() => setFormTermType('expires')}
                        >
                          <IonRadio ref={expiresRadioRef} value="expires" className={styles.radioCircle} />
                          <IonRow className={styles.fBillingDayCardText}>
                            <p className={styles.fBillingDayCardTitle}>{tr('components.HelcimPlansModal.expire')}</p>
                            <p className={styles.fBillingDayCardSub}>{tr('components.HelcimPlansModal.planEndsAfterSetOfCycles')}</p>
                          </IonRow>
                        </button>
                      </IonRow>

                      {formTermType === 'expires' && (
                        <>
                          <IonRow className={styles.fSubDivider} />
                          <p className={styles.fSubHeading}>{tr('components.HelcimPlansModal.defaultTermLength')}</p>
                          <p className={styles.fSubSubtitle}>{tr('components.HelcimPlansModal.setTheNumberOfBillingCycles')}</p>
                          <IonRow className={styles.fStepperWrap}>
                            <label className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.billingCycle')}</label>
                            <input
                              type="number" min="1"
                              className={styles.fStepperInput}
                              value={formTermLength}
                              onChange={e => setFormTermLength(String(Math.max(1, parseInt(e.target.value) || 1)))}
                            />
                            <IonRow className={styles.fStepperArrows}>
                              <button type="button" className={styles.fStepperArrowBtn} style={{ WebkitTapHighlightColor: 'transparent' }} onClick={() => setFormTermLength(v => String((parseInt(v) || 1) + 1))}><IonIcon icon={chevronUpOutline} /></button>
                              <button type="button" className={styles.fStepperArrowBtn} style={{ WebkitTapHighlightColor: 'transparent' }} onClick={() => setFormTermLength(v => String(Math.max(1, (parseInt(v) || 1) - 1)))}><IonIcon icon={chevronDownOutline} /></button>
                            </IonRow>
                          </IonRow>
                        </>
                      )}
                    </>
                  )}
                </IonCard>

                {/* ── More Options ── */}
                <IonCard className={styles.fCard}>
                  <button className={styles.fMoreHeader} onClick={() => setFormMoreOpen(v => !v)}>
                    <p className={styles.fSectionTitle} style={{ margin: 0 }}>{tr('components.HelcimPlansModal.moreOptions')}</p>
                    <IonIcon icon={formMoreOpen ? chevronUpOutline : chevronDownOutline} className={styles.fMoreChevron} />
                  </button>

                  {formMoreOpen && (
                    <>
                      {/* Payment Method */}
                      <IonRow className={styles.fSubDivider} />
                      <p className={styles.fSubHeading}>{tr('components.HelcimPlansModal.paymentMethod')}</p>
                      <p className={styles.fSubSubtitle}>{tr('components.HelcimPlansModal.selectThePaymentMethodYouWant')}</p>
                      <IonRow className={styles.fCheckRow} onClick={() => setFormPayCard(v => !v)}>
                        <IonCheckbox checked={formPayCard} className={styles.fCheckbox} />
                        <span>{tr('components.HelcimPlansModal.creditCard')}</span>
                      </IonRow>
                      <IonRow className={styles.fCheckRow} onClick={() => setFormPayACH(v => !v)}>
                        <IonCheckbox checked={formPayACH} className={styles.fCheckbox} />
                        <span>{tr('components.HelcimPlansModal.achPayment')}</span>
                      </IonRow>

                      {/* Tax Setting — standalone <ion-radio> rows (no
                      IonRadioGroup), same pattern as the billing-day/term
                      cards above. */}
                      <IonRow className={styles.fSubDivider} />
                      <p className={styles.fSubHeading}>{tr('components.HelcimPlansModal.taxSetting')}</p>
                      <p className={styles.fSubSubtitle}>{tr('components.HelcimPlansModal.chooseHowThisPlanIsTaxed')}</p>
                      <IonRow className={styles.fRadioRow} onClick={() => setFormTaxType('no_tax')}>
                        <IonRadio ref={taxNoRadioRef} value="no_tax" className={styles.radioCircle} />
                        <span>{tr('components.HelcimPlansModal.noTax')}</span>
                      </IonRow>
                      <IonRow className={styles.fRadioRow} onClick={() => setFormTaxType('customer')}>
                        <IonRadio ref={taxCustomerRadioRef} value="customer" className={styles.radioCircle} />
                        <span>{tr('components.HelcimPlansModal.basedOnCustomerSLocation')}</span>
                      </IonRow>
                      <IonRow className={styles.fRadioRow} onClick={() => setFormTaxType('merchant')}>
                        <IonRadio ref={taxMerchantRadioRef} value="merchant" className={styles.radioCircle} />
                        <span>{tr('components.HelcimPlansModal.basedOnMerchantSLocation')}</span>
                      </IonRow>
                      {formTaxType === 'merchant' && (
                        <button
                          ref={taxCalcTriggerRef}
                          type="button"
                          className={styles.fSelectWrapFull}
                          onClick={() => setShowTaxCalcDropdown(v => !v)}
                        >
                          <span className={styles.fFloatLabel}>{tr('components.HelcimPlansModal.type')}</span>
                          <span className={styles.fSelectValue}>{optLabel(TAX_CALC_OPTIONS, formTaxCalc)}</span>
                          <IonIcon icon={chevronDownOutline} className={styles.fSelectChevron} />
                        </button>
                      )}

                      {/* Notification Preferences */}
                      <IonRow className={styles.fSubDivider} />
                      <p className={styles.fSubHeading}>{tr('components.HelcimPlansModal.notificationPreferences')}</p>
                      <p className={styles.fSubSubtitle}>{tr('components.HelcimPlansModal.selectWhatNotificationEmailsYouWould')}</p>
                      <IonCard className={styles.fNotifTable}>
                        <IonRow className={styles.fNotifHeader}>
                          <span>{tr('components.HelcimPlansModal.emailType')}</span>
                          <span>{tr('components.HelcimPlansModal.merchant')}</span>
                        </IonRow>
                        <IonRow className={styles.fNotifDivider} />
                        <IonRow
                          className={styles.fNotifRow}
                          onClick={() => {
                            const next = !formNotifRecurring;
                            setFormNotifRecurring(next);
                            if (!next) setFormBusinessEmail('');
                          }}
                        >
                          <span>{tr('components.HelcimPlansModal.recurringQueue')}</span>
                          <IonCheckbox checked={formNotifRecurring} className={styles.fCheckbox} />
                        </IonRow>
                        {formNotifRecurring && (
                          <IonRow className={styles.fNotifRow}>
                            <input
                              type="email"
                              className={styles.fEmailInput}
                              placeholder={tr('components.HelcimPlansModal.merchantEmailAddress')}
                              value={formBusinessEmail}
                              onChange={e => setFormBusinessEmail(e.target.value)}
                            />
                          </IonRow>
                        )}
                      </IonCard>
                    </>
                  )}
                </IonCard>

                <IonRow style={{ height: 48 }} />
              </IonGrid>
            </IonContent>
          </BasePageWithHeader>
          {pricingDialogOpen && (
            <IonRow className={styles.pricingOverlay}>
              <IonCard className={styles.pricingDialog}>
                <IonRow className={styles.pricingHeader}>
                  <button className={styles.pricingClose} onClick={() => setPricingDialogOpen(false)}>
                    <IonIcon icon={closeOutline} />
                  </button>
                  <span className={styles.pricingTitle}>{tr('components.HelcimPlansModal.updatePricing')}</span>
                </IonRow>
                <p className={styles.pricingBody}>{tr('components.HelcimPlansModal.youAreUpdatingTheBaseRate')}</p>
                <IonRow className={styles.pricingOptions}>
                  <IonRow
                    className={`${styles.pricingOption} ${pricingChoice === 'future' ? styles.pricingOptionSelected : ''}`}
                    onClick={() => setPricingChoice('future')}
                  >
                    <IonRadio ref={pricingFutureRadioRef} value="future" className={styles.radioCircle} />
                    <span>{tr('components.HelcimPlansModal.forFutureSubscriptionsOnly')}</span>
                  </IonRow>
                  <IonRow
                    className={`${styles.pricingOption} ${pricingChoice === 'all' ? styles.pricingOptionSelected : ''}`}
                    onClick={() => setPricingChoice('all')}
                  >
                    <IonRadio ref={pricingAllRadioRef} value="all" className={styles.radioCircle} />
                    <span>{tr('components.HelcimPlansModal.forAllSubscriptions')}</span>
                  </IonRow>
                </IonRow>
                <IonRow className={styles.pricingActions}>
                  <IonButton fill="clear" className={styles.pricingCancelBtn} onClick={() => setPricingDialogOpen(false)}>{tr('components.HelcimPlansModal.cancel')}</IonButton>
                  <IonButton fill="solid" color="primary" className={styles.pricingUpdateBtn} onClick={() => { setPricingDialogOpen(false); handleSaveForm(pricingChoice === 'all'); }}>{tr('components.HelcimPlansModal.update')}</IonButton>
                </IonRow>
              </IonCard>
            </IonRow>
          )}
        </IonRow>
      </IonModal>
    </>
  );
};

export default HelcimPlansModal;